/**
 * BgtEvdncDialog.tsx
 * ------------------------------------------------------------
 * - 운영비(MBSD) / 특별회계(SPC) 공용
 * - 기존 운영비 props(mbsdsExpndDocId, expndEvdncId) 그대로 유지
 * - 특별회계용 props 추가: spcAcntgExpndDocId, spcAcntgExpndEvdncId
 * - mode 없으면 자동 판별(특별회계 docId가 있으면 SPC)
 * - 특별회계 상세 조회 URL이 다를 수 있으니 evdncFetchUrl로 override 가능
 */

import React from "react";
import AppContext from "@/js/common/AppContext";
import Axios from "@/js/common/Axios";
import Util from "@/js/common/Util";

import DatePickerBox from "@/js/common/ui/input/DatePickerBox";
import NumberTextBox from "@/js/common/ui/input/NumberTextBox";
import TextBox from "@/js/common/ui/input/TextBox";
import Button from "@/js/common/ui/Button";

import BudgetViewFile from "../../budgetFileBox/BudgetViewFile";
import { budgetFilesProps } from "@/js/common/types/types";

type EvdncMode = "MBSD" | "SPC";

interface Props {
  // ===== 운영비(기존) =====
  mbsdsExpndDocId?: string;
  expndEvdncId?: string;

  // ===== 특별회계(추가) =====
  spcAcntgExpndDocId?: string;
  spcAcntgExpndEvdncId?: string;

  // ===== 공용(추가) =====
  mode?: EvdncMode;              // 없으면 자동 판별
  evdncFetchUrl?: string;        // 서버 URL이 다르면 여기로 강제 지정

  setModalOpen: Function;
}

const BgtEvdncDialog = ({
  mbsdsExpndDocId,
  expndEvdncId: expndEvdncIdProp,
  spcAcntgExpndDocId,
  spcAcntgExpndEvdncId,
  mode: modeProp,
  evdncFetchUrl,
  setModalOpen,
}: Props) => {
  const mode: EvdncMode = modeProp ?? (Util.isNotEmpty(spcAcntgExpndDocId) ? "SPC" : "MBSD");

  // 실제로 사용할 docId/evdncId
  const docId = mode === "SPC" ? String(spcAcntgExpndDocId) : String(mbsdsExpndDocId);
  const evdncId = mode === "SPC" ? String(spcAcntgExpndEvdncId) : String(expndEvdncIdProp);

  const [useDt, setUseDt] = React.useState<string>("");
  const [usePrpsCn, setUsePrpsCn] = React.useState<string>("");
  const [useCn, setUseCn] = React.useState<string>("");
  const [useAmt, setUseAmt] = React.useState<string>("");
  const [etcCn, setEtcCn] = React.useState<string>("");
  const [rmrkCn, setRmrkCn] = React.useState<string>("");
  const [savedEvdncFiles, setSavedEvdncFiles] = React.useState<budgetFilesProps[]>([]);

  const resetFormOnly = () => {
    setUseDt("");
    setUsePrpsCn("");
    setUseCn("");
    setUseAmt("");
    setEtcCn("");
    setRmrkCn("");
    setSavedEvdncFiles([]);
  };

  const fetchEvdnc = () => {
    if (Util.isEmpty(docId)) {
      resetFormOnly();
      return;
    }

    const defaultUrl =
      mode === "MBSD"
        ? "system/bgt/mbsds/expnd/select-bgt-evdnc.do"
        : "system/bgt/spc/expnd/select-bgt-spc-evdnc.do"; 
    const url = evdncFetchUrl || defaultUrl;

    AppContext.showSpinner();
    Axios.dataAccess({
      url,
      methodType: "post",
      paramType: "json",
      paramData:
        mode === "MBSD"
          ? {
              mbsdsExpndDocId: docId,
              expndEvdncId: Util.isEmpty(evdncId) ? null : evdncId,
            }
          : {
              spcAcntgExpndDocId: docId,
              spcAcntgExpndEvdncId: Util.isEmpty(evdncId) ? null : evdncId,
            },
      onSuccessFn: (res: any) => {
        const result = res?.result || {};
        const item = result?.item;

        if (Util.isEmpty(item)) {
          resetFormOnly();
          AppContext.hideSpinner();
          return;
        }

        setUseDt(String(item.useDt));
        setUsePrpsCn(String(item.usePrpsCn));
        setUseCn(String(item.useCn));
        setUseAmt(Util.comma(String(item.useAmt ?? 0)));
        setEtcCn(String(item.etcCn));
        setRmrkCn(String(item.rmrkCn));

        setSavedEvdncFiles(result?.listFile ?? []);

        AppContext.hideSpinner();
      },
    });
  };

  React.useEffect(() => {
    // evdncId가 없으면 초기화 후 docId 기준으로 다시 조회(상세가 '단건/첫건' 정책이면 서버가 처리)
    if (Util.isEmpty(evdncId)) resetFormOnly();
    fetchEvdnc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, docId, evdncId]);

  const closeModal = () => setModalOpen(false);

  return (
    <div className="pb10 pl10 pr10">
      <div className="dpFlx gap20">
        {/* 사용일자 */}
        <div className="w210">
          <div className="h30 dpFlx aiE pb5 bdBox">
            <span className="fm-b fs13 ls06 fc111111">사용일자</span>
          </div>
          <DatePickerBox
            value={useDt}
            width={210}
            height={36}
            fontSize={13}
            fontFamily="fm-c"
            color="#222"
            letterSpacing={-0.5 as any}
            borderColor="#cfcfcf"
            borderOnColor="#222"
            borderRadius={5}
            bgColor="#F6F6F6"
            textAlign="left"
            calendarThemeColor={AppContext.themeColor}
            onChange={(v: string) => setUseDt(v)}
            disabled={true}
          />
        </div>

        {/* 금액 */}
        <div className="w210 ml12">
          <div className="h30 dpFlx aiE pb5 bdBox">
            <span className="fm-b fs13 ls06 fc111111">금액</span>
          </div>
          <div className="h36 dpFlx aiC bd1 bdrd5 bdcCFCFCF bgcFFE6CE bsBdb">
            <NumberTextBox
              value={useAmt}
              className="w100p h100p ml10 taR"
              thousandsGroupStyle="thousand"
              thousandSeparator=","
              placeholder="0"
              fontSize={13}
              fontFamily="fm-c"
              letterSpacing={-0.5 as any}
              color="#222"
              inputClass={"taR mr10"}
              disabled={true}
              onChange={(v: string) => setUseAmt(v)}
            />
          </div>
        </div>
      </div>

      {/* 사용목적 */}
      <div className="w100p">
        <div className="h30 dpFlx aiE pb5 bdBox">
          <span className="fm-b fs13 ls06 fc111111">사용목적</span>
        </div>
        <TextBox
          value={usePrpsCn}
          width={454}
          height={36}
          fontSize={13}
          fontFamily="fm-c"
          color="#222"
          letterSpacing={-0.5 as any}
          borderColor="#cfcfcf"
          borderOnColor="#222"
          borderRadius={5}
          style={{ backgroundColor: "#F6F6F6", padding: "0 10px" }}
          onChangeFunc={(t: string) => setUsePrpsCn(t)}
          mode={"disabled"}
        />
      </div>

      {/* 사용내용 */}
      <div className="mt10">
        <div className="h30 dpFlx aiE pb5 bdBox">
          <span className="fm-b fs13 ls06 fc111111">내용</span>
        </div>
        <div className="flxg1 bsBdb">
          <textarea
            value={useCn}
            className="w100p h100 bsBdb p10 bdrd5 bd1 bdcDFDFDF bdrd2 fm-c fs13 fc333333 ls1 lh20 bgcF6F6F6 rszN"
            maxLength={2000}
            placeholder=""
            onChange={(e) => setUseCn(e.target.value)}
            disabled={true}
          />
        </div>
      </div>

      {/* 기타내용 */}
      <div className="">
        <div className="h30 dpFlx aiE pb5 bdBox">
          <span className="fm-b fs13 ls06 fc111111">기타 내용</span>
        </div>
        <div className="flxg1 bsBdb">
          <textarea
            value={etcCn}
            className="w100p h50 bsBdb p10 bdrd5 bd1 bdcDFDFDF bdrd5 fm-c fs13 fc333333 ls1 lh20 bgcF6F6F6 rszN"
            maxLength={2000}
            placeholder=""
            onChange={(e) => setEtcCn(e.target.value)}
            disabled={true}
          />
        </div>
      </div>

      {/* 비고 */}
      <div className="mt10">
        <div className="h30 dpFlx aiE pb5 bdBox">
          <span className="fm-b fs13 ls06 fc111111">비고</span>
        </div>
        <div className="flxg1 bsBdb">
          <textarea
            value={rmrkCn}
            className="w100p h50 bsBdb p10 bdrd5 bd1 bdcDFDFDF bdrd2 fm-c fs13 fc333333 ls1 lh20 bgcF6F6F6 rszN"
            maxLength={2000}
            placeholder=""
            onChange={(e) => setRmrkCn(e.target.value)}
            disabled={true}
          />
        </div>
      </div>

      {/* 파일(공용) */}
      <BudgetViewFile fileSeCd={mode === "MBSD" ? "A03" : "B03"} savedBudgetFiles={savedEvdncFiles} width="450px" height="70px" />

      <div className="dpFlx aiC jcE mt25 gap10">
        <Button
          width={80}
          height={40}
          fontFamily="fm-b"
          fontSize={16}
          letterSpacing={-1}
          variant="styCloseBtn"
          borderRadius={6}
          buttonClass="dpFlx aiC jcC"
          buttonName="닫기"
          onClickFunc={closeModal}
        />
      </div>
    </div>
  );
};

export default BgtEvdncDialog;
